import axios from 'axios';
import { api } from './api';

export interface SKUData {
  file: File;
}

export interface SKUSingle {
  image: File;
  sku_number: string;
  name: string;
  presentation: string;
  brand: string;
  volume: string;
  quantity: string;
  tipology: string;
  size: string;
}

export interface SKUListItem {
  id: string;
  checked: boolean;
  disabled: boolean;
  data: {
    imagen: string;
    sku_number: string;
    nombre: string;
    descripcion: string;
    marca: string;
    presentacion: string;
    size: string;
    tipologia: string;
    fecha_creacion: string;
  };
}

export interface SKUListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SKUListItem[];
}

export interface SKUListParams {
  page?: number;
  page_size?: number;
  brand?: string;
  tipology?: string;
  search?: string;
  ordering?: string;
}

export interface GetSKUDataResponse {
  image: string;
  sku_number: string;
  name: string;
  description: string;
  brand: string;
  presentation: string;
  volume: string;
  quantity: string;
  size: string;
  tipology: string;
  created_at: string;
}

export const skuService = {
  getSKUList: async (params?: SKUListParams): Promise<SKUListResponse> => {
    try {
      const response = await api.get('/skus/list', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener lista de SKUs:', error);

      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Error al comunicarse con el servidor');
      }

      throw new Error('Error inesperado al obtener la lista de SKUs');
    }
  },

  saveSKU: async (skuData: SKUData): Promise<{ success: boolean; message: string }> => {
    try {
      const formData = new FormData();
      formData.append('file', skuData.file);

      const response = await api.post('/skus', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al guardar SKU:', error);

      if (axios.isAxiosError(error)) {
        console.log(error);
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor'
        };
      }

      return {
        success: false,
        message: 'Error inesperado al guardar el SKU'
      };
    }
  },

  saveSingleSKU: async (skuData: SKUSingle): Promise<any> => {
    const formData = new FormData();
    for (const key in skuData) {
      formData.append(key, skuData[key as keyof SKUSingle]);
    }

    try {
      const response = await api.post('/sku', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al guardar SKU:', error);

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: (typeof error.response?.data?.error === 'string'
            ? error.response?.data?.error
            : Object.values(error.response?.data?.error || {})[0]) || 'Error al comunicarse con el servidor'
        };
      }

      return {
        success: false,
        message: 'Error inesperado al guardar el SKU'
      };
    }
  },

  updateSKU: async (skuId: string, skuData: Omit<SKUData, 'file'>): Promise<{ success: boolean; message: string }> => {
    try {
      const formData = new FormData();
      for (const key in skuData) {
        formData.append(key, skuData[key as keyof Omit<SKUData, 'file'>]);
      }

      const response = await api.put(`/skus/${skuId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al actualizar SKU:', error);

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor'
        };
      }

      return {
        success: false,
        message: 'Error inesperado al actualizar el SKU'
      };
    }
  },

  deleteSKU: async (skuId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.delete(`/skus/${skuId}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al eliminar SKU:', error);

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor'
        };
      }

      return {
        success: false,
        message: 'Error inesperado al eliminar el SKU'
      };
    }
  },
  getSKU: async (skuId: string): Promise<{ success: boolean; message: string, data?: GetSKUDataResponse }> => {
    try {
      const response = await api.get(`/skus/${skuId}`);
      return {
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al obtener SKU:', error);

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor',
        };
      }

      return {
        success: false,
        message: 'Error inesperado al obtener el SKU'
      };
    }
  },

  exportSKUs: async (skuIds?: (string | number)[], format: 'xlsx' | 'csv' = 'xlsx'): Promise<{ success: boolean; task_id?: string; message?: string }> => {
    try {
      const body: Record<string, unknown> = { format };
      if (skuIds && skuIds.length > 0) {
        body.sku_ids = skuIds.map(Number);
      }
      const response = await api.post('/export-skus', body);
      return { success: true, task_id: response.data.task_id };
    } catch (error) {
      console.error('Error al solicitar exportación de SKUs:', error);
      if (axios.isAxiosError(error)) {
        return { success: false, message: error.response?.data?.error || 'Error al solicitar exportación' };
      }
      return { success: false, message: 'Error inesperado al solicitar exportación' };
    }
  },

  getExportStatus: async (taskId: string): Promise<{ status: string; download_url?: string; error?: string }> => {
    try {
      const response = await api.get(`/export-skus/status/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('Error al consultar estado de exportación:', error);
      return { status: 'failed', error: 'Error al consultar estado' };
    }
  },

  downloadExport: async (taskId: string): Promise<Blob | null> => {
    try {
      const response = await api.get(`/export-skus/download/${taskId}`, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      console.error('Error al descargar archivo de exportación:', error);
      return null;
    }
  },

  deleteSKUs: async (skuIds: string[]): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.delete(`/skus/delete-multiple`, { data: { ids: skuIds } });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al eliminar SKUs:', error);

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor'
        };
      }

      return {
        success: false,
        message: 'Error inesperado al eliminar los SKUs'
      };
    }
  }
};
